import { useTranslations } from '@/i18n/use-translations';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Slider,
  Paper,
} from '@mui/material';
import { CloudUploadOutlined, DeleteOutline, UndoOutlined, ImageOutlined, PaletteOutlined } from '@mui/icons-material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { updateEventSettingsSchema, type UpdateEventSettingsForm } from '@/schemas/event/update-event-settings';
import { VisuallyHiddenInput } from '@/components/common/visually-hidden.input';
import { useRef, useEffect, useState, useMemo } from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import { useImagePreview } from '@/hooks/use-image-preview';
import type { GetEventSettingsDto } from '@/types/dtos/events/response/get-event-settings.dto';
import { useControlPreviewMutation } from '@/state/mutations/player/use-control-preview';
import { PreviewModal } from '@/components/preview/preview-modal';
import { useNotifications } from '@/hooks/use-notifications';
import { useEventStore } from '@/state/stores/use-event-store';
import { CustomizeStickyPreview, ensureLiveTranslationPreviewLayer } from './customize-sticky-preview';
import {
  absoluteFontSizeCqhFromRelative,
  DEFAULT_FONT_SIZE_CQH,
  FONT_SIZE_RELATIVE_MAX,
  FONT_SIZE_RELATIVE_MIN,
  relativeFontSizeFromAbsoluteCqh,
} from '@/helpers/preview/font-size-cqh.helper';

const DEFAULT_BG = '#c0c0c0';
const DEFAULT_TEXT = '#000000';
const DEFAULT_CAPTION_TEXT = '#ffffff';
const DEFAULT_OVERLAY = '#000000';
const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function ColorField({
  label,
  value,
  onChange,
  hexLabel,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  hexLabel: string;
}) {
  const [hexDraft, setHexDraft] = useState(value);
  useEffect(() => {
    setHexDraft(value);
  }, [value]);

  return (
    <Stack spacing={0.75}>
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box
          component="label"
          sx={{
            width: 44,
            height: 44,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: HEX_COLOR_RE.test(value) ? value : '#ffffff',
            cursor: 'pointer',
            overflow: 'hidden',
            flexShrink: 0,
            boxShadow: 1,
          }}
        >
          <Box
            component="input"
            type="color"
            value={HEX_COLOR_RE.test(value) ? (value.length === 4 ? expandShortHex(value) : value) : '#ffffff'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={{
              opacity: 0,
              width: '100%',
              height: '100%',
              cursor: 'pointer',
              border: 0,
              p: 0,
            }}
            aria-label={label}
          />
        </Box>
        <TextField
          size="small"
          label={hexLabel}
          value={hexDraft}
          onChange={(e) => {
            const next = e.target.value;
            setHexDraft(next);
            if (HEX_COLOR_RE.test(next)) onChange(next);
          }}
          onBlur={() => {
            if (!HEX_COLOR_RE.test(hexDraft)) setHexDraft(value);
          }}
          sx={{ width: 140 }}
          inputProps={{ spellCheck: false }}
        />
      </Stack>
    </Stack>
  );
}

function expandShortHex(hex: string): string {
  const h = hex.slice(1);
  if (h.length !== 3) return hex;
  return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`;
}

function SectionCard({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function AssetTile({
  title,
  emptyLabel,
  previewUrl,
  onUpload,
  onRemove,
  onUndoRemove,
  removePending,
  removeLabel,
  undoLabel,
  uploadLabel,
  hint,
  inputRef,
  accept = 'image/*',
  onFileChange,
  wide,
  error,
}: {
  title: string;
  emptyLabel: string;
  previewUrl?: string | null;
  onUpload: () => void;
  onRemove?: () => void;
  onUndoRemove?: () => void;
  removePending?: boolean;
  removeLabel?: string;
  undoLabel?: string;
  uploadLabel: string;
  hint?: string;
  inputRef: React.RefObject<HTMLInputElement | null>;
  accept?: string;
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  wide?: boolean;
  error?: string | null;
}) {
  return (
    <Stack spacing={1.25}>
      <Typography variant="body2" fontWeight={600}>
        {title}
      </Typography>
      <Stack
        direction={wide ? 'column' : { xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={wide ? 'stretch' : { sm: 'flex-start' }}
      >
        <Box
          sx={{
            width: wide ? '100%' : 120,
            maxWidth: wide ? 360 : undefined,
            ...(wide ? { aspectRatio: '16 / 9', height: 'auto' } : { height: 120 }),
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: wide ? 'action.hover' : 'grey.600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0,
          }}
        >
          {previewUrl && !removePending ? (
            <Box
              component="img"
              src={previewUrl}
              alt={title}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: wide ? 'cover' : 'contain',
              }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 1 }}>
              {removePending ? removeLabel : emptyLabel}
            </Typography>
          )}
        </Box>
        <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
          {removePending ? (
            <Button
              variant="outlined"
              startIcon={<UndoOutlined />}
              onClick={onUndoRemove}
              sx={{ alignSelf: 'flex-start', height: 'auto', minHeight: 36, whiteSpace: 'nowrap' }}
            >
              {undoLabel}
            </Button>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                startIcon={<CloudUploadOutlined />}
                onClick={onUpload}
                sx={{ height: 'auto', minHeight: 36, whiteSpace: 'nowrap' }}
              >
                {uploadLabel}
              </Button>
              {onRemove && previewUrl && (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutline />}
                  onClick={onRemove}
                  sx={{ height: 'auto', minHeight: 36, whiteSpace: 'nowrap' }}
                >
                  {removeLabel}
                </Button>
              )}
            </Stack>
          )}
          {hint && (
            <Typography variant="caption" color="text.secondary">
              {hint}
            </Typography>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </Stack>
      <VisuallyHiddenInput ref={inputRef} type="file" accept={accept} onChange={onFileChange} />
    </Stack>
  );
}

export default function CustomizeForm({
  onSettingsChange,
  initialSettings,
}: {
  onSettingsChange: (settings: UpdateEventSettingsForm) => void;
  initialSettings?: GetEventSettingsDto;
}) {
  const t = useTranslations();
  const notifications = useNotifications();
  const event = useEventStore((s) => s.event);
  const { mutateAsync: controlPreviewMutation, isPending: isPreviewPending } = useControlPreviewMutation();

  const bgInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const speakerBackgroundInputRef = useRef<HTMLInputElement>(null);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewData, setPreviewData] = useState<{ playType: string; playerSyncData: Record<string, unknown> } | null>(
    null,
  );

  const { createPreview, getPreview } = useImagePreview();

  const convertMarginsForDisplay = (
    margins: { top?: number; bottom?: number; left?: number; right?: number } | undefined,
    mode: 'percent' | 'px',
  ) => {
    if (!margins || mode === 'percent') return margins;
    const converted = { ...margins };
    if (converted.top != null) converted.top = Math.round((converted.top / 100) * 1080);
    if (converted.bottom != null) converted.bottom = Math.round((converted.bottom / 100) * 1080);
    if (converted.left != null) converted.left = Math.round((converted.left / 100) * 1920);
    if (converted.right != null) converted.right = Math.round((converted.right / 100) * 1920);
    return converted;
  };

  const form = useForm<UpdateEventSettingsForm>({
    resolver: async (data) => {
      const result = updateEventSettingsSchema.safeParse(data);
      if (result.success) {
        return { values: result.data, errors: {} };
      }
      const fieldErrors: Record<string, { type: string; message: string }> = {};
      result.error.issues.forEach((error) => {
        const path = error.path.join('.');
        fieldErrors[path] = {
          type: error.code,
          message: error.message,
        };
      });
      return { values: {}, errors: fieldErrors };
    },
    defaultValues: {
      backgroundType: initialSettings?.backgroundType || 'color',
      backgroundColor: initialSettings?.backgroundColor || DEFAULT_BG,
      textColor: initialSettings?.textColor || DEFAULT_TEXT,
      backgroundImage: undefined,
      eventLogoImage: undefined,
      deleteEventLogo: false,
      remoteSpeakerBackgroundImage: undefined,
      deleteRemoteSpeakerBackground: false,
      backgroundOverlay: initialSettings?.backgroundOverlay || false,
      backgroundOverlayColor: initialSettings?.backgroundOverlayColor || DEFAULT_OVERLAY,
      backgroundOverlayTransparency: initialSettings?.backgroundOverlayTransparency || 50,
      fontSizeMultiplier: initialSettings?.fontSizeMultiplier ?? DEFAULT_FONT_SIZE_CQH,
      liveTranslationFontSizeMultiplier:
        initialSettings?.liveTranslationFontSizeMultiplier ??
        initialSettings?.fontSizeMultiplier ??
        DEFAULT_FONT_SIZE_CQH,
      liveTranslationTextColor: initialSettings?.liveTranslationTextColor || DEFAULT_CAPTION_TEXT,
      margins: initialSettings?.margins || {},
    },
  });

  const backgroundType = useWatch({ control: form.control, name: 'backgroundType' }) || 'color';
  const backgroundOverlay = useWatch({ control: form.control, name: 'backgroundOverlay' }) || false;
  const deleteEventLogo = useWatch({ control: form.control, name: 'deleteEventLogo' }) === true;
  const deleteSpeakerBackground = useWatch({ control: form.control, name: 'deleteRemoteSpeakerBackground' }) === true;
  const fontSizeMultiplier = useWatch({ control: form.control, name: 'fontSizeMultiplier' }) ?? DEFAULT_FONT_SIZE_CQH;
  const liveTranslationFontSizeMultiplier =
    useWatch({ control: form.control, name: 'liveTranslationFontSizeMultiplier' }) ??
    fontSizeMultiplier ??
    DEFAULT_FONT_SIZE_CQH;
  const fontSizeRelative = relativeFontSizeFromAbsoluteCqh(fontSizeMultiplier);
  const liveTranslationFontSizeRelative = relativeFontSizeFromAbsoluteCqh(liveTranslationFontSizeMultiplier);

  useEffect(() => {
    if (backgroundType !== 'image' && backgroundOverlay) {
      form.setValue('backgroundOverlay', false);
    }
  }, [backgroundType, backgroundOverlay, form]);

  const [localBackgroundColor, setLocalBackgroundColor] = useState(initialSettings?.backgroundColor || DEFAULT_BG);
  const [localTextColor, setLocalTextColor] = useState(initialSettings?.textColor || DEFAULT_TEXT);
  const [localCaptionTextColor, setLocalCaptionTextColor] = useState(
    initialSettings?.liveTranslationTextColor || DEFAULT_CAPTION_TEXT,
  );
  const [localOverlayColor, setLocalOverlayColor] = useState(
    initialSettings?.backgroundOverlayColor || DEFAULT_OVERLAY,
  );
  const [localOverlayTransparency, setLocalOverlayTransparency] = useState(
    initialSettings?.backgroundOverlayTransparency || 50,
  );
  const [imageValidationError, setImageValidationError] = useState<string | null>(null);
  const [speakerBackgroundError, setSpeakerBackgroundError] = useState<string | null>(null);
  // Decoding finishes asynchronously; a later upload, removal or reset makes its result stale.
  const speakerBackgroundGenerationRef = useRef(0);
  const backgroundImageGenerationRef = useRef(0);
  const [marginsMode, setMarginsMode] = useState<'percent' | 'px'>('percent');

  const debouncedBackgroundColor = useDebounce(localBackgroundColor, 150);
  const debouncedTextColor = useDebounce(localTextColor, 150);
  const debouncedOverlayColor = useDebounce(localOverlayColor, 150);
  const debouncedOverlayTransparency = useDebounce(localOverlayTransparency.toString(), 150);

  useEffect(() => {
    form.setValue('backgroundColor', debouncedBackgroundColor, { shouldDirty: true });
  }, [debouncedBackgroundColor, form]);

  useEffect(() => {
    form.setValue('textColor', debouncedTextColor, { shouldDirty: true });
  }, [debouncedTextColor, form]);

  useEffect(() => {
    form.setValue('backgroundOverlayColor', debouncedOverlayColor, { shouldDirty: true });
  }, [debouncedOverlayColor, form]);

  useEffect(() => {
    form.setValue('backgroundOverlayTransparency', parseInt(debouncedOverlayTransparency || '50', 10), {
      shouldDirty: true,
    });
  }, [debouncedOverlayTransparency, form]);

  useEffect(() => {
    if (initialSettings) {
      speakerBackgroundGenerationRef.current += 1;
      backgroundImageGenerationRef.current += 1;
      const bgColor = initialSettings.backgroundColor || DEFAULT_BG;
      const txtColor = initialSettings.textColor || DEFAULT_TEXT;
      const captionColor = initialSettings.liveTranslationTextColor || DEFAULT_CAPTION_TEXT;
      const overlayColor = initialSettings.backgroundOverlayColor || DEFAULT_OVERLAY;
      const overlayTransparency = initialSettings.backgroundOverlayTransparency || 50;
      queueMicrotask(() => {
        setLocalBackgroundColor(bgColor);
        setLocalTextColor(txtColor);
        setLocalCaptionTextColor(captionColor);
        setLocalOverlayColor(overlayColor);
        setLocalOverlayTransparency(overlayTransparency);
        setMarginsMode('percent');
        createPreview('event-logo', null);
        createPreview('background-image', null);
        createPreview('speaker-background', null);
        setSpeakerBackgroundError(null);
        setImageValidationError(null);
      });
      form.reset({
        backgroundType: initialSettings.backgroundType || 'color',
        backgroundColor: bgColor,
        textColor: txtColor,
        backgroundImage: undefined,
        eventLogoImage: undefined,
        deleteEventLogo: false,
        remoteSpeakerBackgroundImage: undefined,
        deleteRemoteSpeakerBackground: false,
        backgroundOverlay: initialSettings.backgroundOverlay || false,
        backgroundOverlayColor: overlayColor,
        backgroundOverlayTransparency: overlayTransparency,
        fontSizeMultiplier: initialSettings.fontSizeMultiplier ?? DEFAULT_FONT_SIZE_CQH,
        liveTranslationFontSizeMultiplier:
          initialSettings.liveTranslationFontSizeMultiplier ??
          initialSettings.fontSizeMultiplier ??
          DEFAULT_FONT_SIZE_CQH,
        liveTranslationTextColor: captionColor,
        margins: initialSettings.margins || {},
      });
    }
    // createPreview is stable enough for reset side-effects; omit from deps to avoid reset loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when settings payload changes
  }, [initialSettings, form]);

  const formValues = useWatch({ control: form.control });
  useEffect(() => {
    if (formValues && !imageValidationError) {
      onSettingsChange(formValues as UpdateEventSettingsForm);
    }
  }, [formValues, onSettingsChange, imageValidationError]);

  /** Resolves to the validation error, or null for a 1920×1080 image. */
  const imageDimensionsError = (file: File): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(
          img.width === 1920 && img.height === 1080
            ? null
            : `Image must be exactly 1920x1080px. Current dimensions: ${img.width}x${img.height}px`,
        );
      };
      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve('Invalid image file');
      };
      img.src = objectUrl;
    });
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    createPreview('event-logo', file);
    form.setValue('eventLogoImage', file || undefined);
    form.setValue('deleteEventLogo', false);
  };

  const handleRemoveLogo = () => {
    createPreview('event-logo', null);
    form.setValue('eventLogoImage', undefined);
    form.setValue('deleteEventLogo', true);
    if (logoInputRef.current) logoInputRef.current.value = '';
  };

  const handleUndoRemoveLogo = () => {
    form.setValue('deleteEventLogo', false);
  };

  const handleSpeakerBackgroundUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    const generation = ++speakerBackgroundGenerationRef.current;
    // A rejected file is never staged, so its error must not hold back syncing the other settings.
    const error = file ? await imageDimensionsError(file) : null;
    if (generation !== speakerBackgroundGenerationRef.current) return;
    setSpeakerBackgroundError(error);
    if (error) {
      event.target.value = '';
      return;
    }
    createPreview('speaker-background', file);
    form.setValue('remoteSpeakerBackgroundImage', file || undefined);
    form.setValue('deleteRemoteSpeakerBackground', false);
  };

  const handleRemoveSpeakerBackground = () => {
    speakerBackgroundGenerationRef.current += 1;
    setSpeakerBackgroundError(null);
    createPreview('speaker-background', null);
    form.setValue('remoteSpeakerBackgroundImage', undefined);
    form.setValue('deleteRemoteSpeakerBackground', true);
    if (speakerBackgroundInputRef.current) speakerBackgroundInputRef.current.value = '';
  };

  const handleBackgroundUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    const generation = ++backgroundImageGenerationRef.current;
    if (file) {
      const error = await imageDimensionsError(file);
      if (generation !== backgroundImageGenerationRef.current) return;
      setImageValidationError(error);
      if (!error) {
        createPreview('background-image', file);
        form.setValue('backgroundImage', file);
      } else {
        event.target.value = '';
        form.setValue('backgroundImage', undefined);
      }
    } else {
      createPreview('background-image', null);
      form.setValue('backgroundImage', undefined);
      setImageValidationError(null);
    }
  };

  const handlePreviewSettings = async () => {
    try {
      const values = form.getValues();
      const eventId = event?.id;
      if (!eventId) return;

      const response = await controlPreviewMutation({
        entityType: 'Event' as const,
        entityId: eventId,
        contextEntityId: eventId,
        contextEntityType: 'Event' as const,
        playType: 'event-info',
      });

      const backgroundImageUrl =
        values.backgroundType === 'image'
          ? getPreview('background-image') || initialSettings?.backgroundImageUrl
          : undefined;
      const deleteLogo = values.deleteEventLogo === true && !values.eventLogoImage;
      const eventLogoImageUrl = deleteLogo ? undefined : getPreview('event-logo') || initialSettings?.eventLogoImageUrl;

      setPreviewData({
        playType: 'event-info',
        playerSyncData: {
          ...response.playerSyncData,
          settings: {
            backgroundType: values.backgroundType,
            backgroundColor: values.backgroundColor,
            textColor: values.textColor,
            backgroundImageUrl,
            eventLogoImageUrl,
            backgroundOverlay: values.backgroundOverlay,
            backgroundOverlayColor: values.backgroundOverlayColor,
            backgroundOverlayTransparency: values.backgroundOverlayTransparency,
            fontSizeMultiplier: values.fontSizeMultiplier,
            liveTranslationFontSizeMultiplier: values.liveTranslationFontSizeMultiplier ?? values.fontSizeMultiplier,
            liveTranslationTextColor: values.liveTranslationTextColor || DEFAULT_CAPTION_TEXT,
            margins: values.margins,
          },
          layers: ensureLiveTranslationPreviewLayer(
            { layers: (response.playerSyncData as { layers?: unknown }).layers },
            values.liveTranslationFontSizeMultiplier ?? values.fontSizeMultiplier,
            values.liveTranslationTextColor || DEFAULT_CAPTION_TEXT,
          ).layers,
        },
      });
      setPreviewModalOpen(true);
      notifications.notifySuccess(t('event.detail.settingsMenu.customize.previewSuccess'));
    } catch (error) {
      console.error('Preview settings error:', error);
      notifications.notifyError(t('event.detail.settingsMenu.customize.previewError'));
    }
  };

  const logoPreviewUrl = deleteEventLogo
    ? null
    : getPreview('event-logo') || initialSettings?.eventLogoImageUrl || null;
  const backgroundPreviewUrl = getPreview('background-image') || initialSettings?.backgroundImageUrl || null;
  const speakerBackgroundPreviewUrl = deleteSpeakerBackground
    ? null
    : getPreview('speaker-background') || initialSettings?.remoteSpeakerBackgroundUrl || null;
  const hasSpeakerBackground = Boolean(
    initialSettings?.remoteSpeakerBackgroundUrl || formValues?.remoteSpeakerBackgroundImage,
  );

  const typedFormValues = useMemo(
    () => (formValues ?? form.getValues()) as UpdateEventSettingsForm,
    [formValues, form],
  );

  // Drive the sticky preview from local color state so updates are instant (not debounce-gated).
  const stickyPreviewFormValues = useMemo(
    () =>
      ({
        ...typedFormValues,
        backgroundColor: localBackgroundColor,
        textColor: localTextColor,
        liveTranslationTextColor: localCaptionTextColor,
        backgroundOverlayColor: localOverlayColor,
        backgroundOverlayTransparency: localOverlayTransparency,
      }) as UpdateEventSettingsForm,
    [
      typedFormValues,
      localBackgroundColor,
      localTextColor,
      localCaptionTextColor,
      localOverlayColor,
      localOverlayTransparency,
    ],
  );

  const marginField = (
    side: 'top' | 'bottom' | 'left' | 'right',
    displayMargins: { top?: number; bottom?: number; left?: number; right?: number } | undefined,
    onChange: (next: UpdateEventSettingsForm['margins']) => void,
    current: UpdateEventSettingsForm['margins'],
  ) => (
    <TextField
      type="number"
      size="small"
      inputProps={{ min: 0, step: 1 }}
      value={displayMargins?.[side] ?? ''}
      onChange={(e) => {
        const raw = e.target.value;
        if (!raw) {
          onChange({ ...current, [side]: undefined });
          return;
        }
        const parsed = parseInt(raw, 10);
        const divisor = marginsMode === 'px' ? (side === 'left' || side === 'right' ? 1920 : 1080) : 1;
        const stored = marginsMode === 'px' ? (parsed / divisor) * 100 : parsed;
        onChange({ ...current, [side]: stored });
      }}
      sx={{ width: 88 }}
      InputProps={{
        endAdornment: (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {marginsMode === 'percent'
              ? t('event.detail.settingsMenu.customize.unitPercent')
              : t('event.detail.settingsMenu.customize.unitPx')}
          </Typography>
        ),
      }}
    />
  );

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.15fr) minmax(280px, 0.85fr)' },
        gap: 2.5,
        alignItems: 'start',
      }}
    >
      <Stack spacing={2}>
        <Typography variant="h6">{t('event.detail.settingsMenu.customize.title')}</Typography>

        <SectionCard title={t('event.detail.settingsMenu.customize.brandSection')}>
          <AssetTile
            title={t('event.detail.settingsMenu.customize.logoLabel')}
            emptyLabel={t('event.detail.settingsMenu.customize.noLogo')}
            previewUrl={logoPreviewUrl}
            onUpload={() => logoInputRef.current?.click()}
            onRemove={initialSettings?.eventLogoImageUrl || formValues?.eventLogoImage ? handleRemoveLogo : undefined}
            onUndoRemove={handleUndoRemoveLogo}
            removePending={deleteEventLogo}
            removeLabel={
              deleteEventLogo
                ? t('event.detail.settingsMenu.customize.logoRemovedPending')
                : t('event.detail.settingsMenu.customize.removeLogo')
            }
            undoLabel={t('event.detail.settingsMenu.customize.undoRemoveLogo')}
            uploadLabel={
              initialSettings?.eventLogoImageUrl || formValues?.eventLogoImage
                ? t('event.detail.settingsMenu.customize.changeLogo')
                : t('event.detail.settingsMenu.customize.selectLogo')
            }
            inputRef={logoInputRef}
            onFileChange={handleLogoUpload}
          />
          <AssetTile
            title={t('event.detail.settingsMenu.customize.speakerBackgroundLabel')}
            emptyLabel={t('event.detail.settingsMenu.customize.noSpeakerBackground')}
            previewUrl={speakerBackgroundPreviewUrl}
            onUpload={() => speakerBackgroundInputRef.current?.click()}
            onRemove={hasSpeakerBackground ? handleRemoveSpeakerBackground : undefined}
            onUndoRemove={() => form.setValue('deleteRemoteSpeakerBackground', false)}
            removePending={deleteSpeakerBackground}
            removeLabel={
              deleteSpeakerBackground
                ? t('event.detail.settingsMenu.customize.speakerBackgroundRemovedPending')
                : t('event.detail.settingsMenu.customize.removeSpeakerBackground')
            }
            undoLabel={t('event.detail.settingsMenu.customize.undoRemoveLogo')}
            uploadLabel={
              hasSpeakerBackground
                ? t('event.detail.settingsMenu.customize.changeSpeakerBackground')
                : t('event.detail.settingsMenu.customize.selectSpeakerBackground')
            }
            hint={t('event.detail.settingsMenu.customize.speakerBackgroundHint')}
            inputRef={speakerBackgroundInputRef}
            onFileChange={(e) => void handleSpeakerBackgroundUpload(e)}
            wide
            error={speakerBackgroundError}
          />
        </SectionCard>

        <SectionCard title={t('event.detail.settingsMenu.customize.backgroundSection')}>
          <Stack spacing={2.5}>
            <Controller
              name="backgroundType"
              control={form.control}
              render={({ field }) => (
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={field.value || 'color'}
                  onChange={(_e, value: 'color' | 'image' | null) => {
                    if (value) field.onChange(value);
                  }}
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 2,
                      textTransform: 'none',
                      gap: 0.75,
                    },
                  }}
                >
                  <ToggleButton value="color">
                    <PaletteOutlined fontSize="small" />
                    {t('event.detail.settingsMenu.customize.types.color')}
                  </ToggleButton>
                  <ToggleButton value="image">
                    <ImageOutlined fontSize="small" />
                    {t('event.detail.settingsMenu.customize.types.image')}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />

            {backgroundType === 'color' && (
              <ColorField
                label={t('event.detail.settingsMenu.customize.backgroundColor')}
                value={localBackgroundColor}
                onChange={setLocalBackgroundColor}
                hexLabel={t('event.detail.settingsMenu.customize.hexColor')}
              />
            )}

            {backgroundType === 'image' && (
              <Stack spacing={2}>
                <AssetTile
                  title={t('event.detail.settingsMenu.customize.types.image')}
                  emptyLabel={t('event.detail.settingsMenu.customize.noBackgroundImage')}
                  previewUrl={backgroundPreviewUrl}
                  onUpload={() => bgInputRef.current?.click()}
                  uploadLabel={
                    initialSettings?.backgroundImageUrl || formValues?.backgroundImage
                      ? t('event.detail.settingsMenu.customize.changeBackgroundImage')
                      : t('event.detail.settingsMenu.customize.selectBackgroundImage')
                  }
                  hint={t('event.detail.settingsMenu.customize.backgroundImageHint')}
                  inputRef={bgInputRef}
                  onFileChange={handleBackgroundUpload}
                  wide
                  error={imageValidationError}
                />

                <Controller
                  name="backgroundOverlay"
                  control={form.control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Switch checked={Boolean(field.value)} onChange={(_e, checked) => field.onChange(checked)} />
                      }
                      label={t('event.detail.settingsMenu.customize.overlay')}
                    />
                  )}
                />

                {backgroundOverlay && (
                  <Stack spacing={2} sx={{ pl: { sm: 1 } }}>
                    <ColorField
                      label={t('event.detail.settingsMenu.customize.overlayColor')}
                      value={localOverlayColor}
                      onChange={setLocalOverlayColor}
                      hexLabel={t('event.detail.settingsMenu.customize.hexColor')}
                    />
                    <Box>
                      <Typography variant="body2" fontWeight={600} gutterBottom>
                        {t('event.detail.settingsMenu.customize.overlayTransparency')}: {localOverlayTransparency}%
                      </Typography>
                      <Slider
                        value={localOverlayTransparency}
                        onChange={(_e, value) => setLocalOverlayTransparency(value as number)}
                        min={0}
                        max={100}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${value}%`}
                        sx={{ maxWidth: 360 }}
                      />
                    </Box>
                  </Stack>
                )}
              </Stack>
            )}
          </Stack>
        </SectionCard>

        <SectionCard title={t('event.detail.settingsMenu.customize.typographySection')}>
          <Stack spacing={2}>
            <ColorField
              label={t('event.detail.settingsMenu.customize.textColor')}
              value={localTextColor}
              onChange={setLocalTextColor}
              hexLabel={t('event.detail.settingsMenu.customize.hexColor')}
            />
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2" fontWeight={600}>
                  {t('event.detail.settingsMenu.customize.fontSizeMultiplier')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {fontSizeRelative.toFixed(1)}×
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('event.detail.settingsMenu.customize.fontSizeHint')}
              </Typography>
              <Controller
                name="fontSizeMultiplier"
                control={form.control}
                render={({ field }) => {
                  const relative = relativeFontSizeFromAbsoluteCqh(
                    typeof field.value === 'number' ? field.value : DEFAULT_FONT_SIZE_CQH,
                  );
                  return (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Slider
                        value={relative}
                        onChange={(_e, value) => field.onChange(absoluteFontSizeCqhFromRelative(value as number))}
                        min={FONT_SIZE_RELATIVE_MIN}
                        max={FONT_SIZE_RELATIVE_MAX}
                        step={0.1}
                        marks={[
                          { value: 0.5, label: '0.5×' },
                          { value: 1, label: '1.0×' },
                          { value: 2, label: '2.0×' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${Number(value).toFixed(1)}×`}
                        sx={{ flex: 1, maxWidth: 320 }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        value={Number(relative.toFixed(1))}
                        onChange={(e) => {
                          if (!e.target.value) {
                            field.onChange(DEFAULT_FONT_SIZE_CQH);
                            return;
                          }
                          field.onChange(absoluteFontSizeCqhFromRelative(parseFloat(e.target.value)));
                        }}
                        inputProps={{
                          step: 0.1,
                          min: FONT_SIZE_RELATIVE_MIN,
                          max: FONT_SIZE_RELATIVE_MAX,
                        }}
                        sx={{ width: 88 }}
                      />
                    </Stack>
                  );
                }}
              />
            </Box>
            <Box>
              <ColorField
                label={t('event.detail.settingsMenu.customize.liveTranslationTextColor')}
                value={localCaptionTextColor}
                onChange={(next) => {
                  setLocalCaptionTextColor(next);
                  form.setValue('liveTranslationTextColor', next, { shouldDirty: true });
                }}
                hexLabel={t('event.detail.settingsMenu.customize.hexColor')}
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                {t('event.detail.settingsMenu.customize.liveTranslationTextColorHint')}
              </Typography>
            </Box>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography variant="body2" fontWeight={600}>
                  {t('event.detail.settingsMenu.customize.liveTranslationFontSizeMultiplier')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {liveTranslationFontSizeRelative.toFixed(1)}×
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {t('event.detail.settingsMenu.customize.liveTranslationFontSizeHint')}
              </Typography>
              <Controller
                name="liveTranslationFontSizeMultiplier"
                control={form.control}
                render={({ field }) => {
                  const relative = relativeFontSizeFromAbsoluteCqh(
                    typeof field.value === 'number' ? field.value : DEFAULT_FONT_SIZE_CQH,
                  );
                  return (
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Slider
                        value={relative}
                        onChange={(_e, value) => field.onChange(absoluteFontSizeCqhFromRelative(value as number))}
                        min={FONT_SIZE_RELATIVE_MIN}
                        max={FONT_SIZE_RELATIVE_MAX}
                        step={0.1}
                        marks={[
                          { value: 0.5, label: '0.5×' },
                          { value: 1, label: '1.0×' },
                          { value: 2, label: '2.0×' },
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${Number(value).toFixed(1)}×`}
                        sx={{ flex: 1, maxWidth: 320 }}
                      />
                      <TextField
                        type="number"
                        size="small"
                        value={Number(relative.toFixed(1))}
                        onChange={(e) => {
                          if (!e.target.value) {
                            field.onChange(DEFAULT_FONT_SIZE_CQH);
                            return;
                          }
                          field.onChange(absoluteFontSizeCqhFromRelative(parseFloat(e.target.value)));
                        }}
                        inputProps={{
                          step: 0.1,
                          min: FONT_SIZE_RELATIVE_MIN,
                          max: FONT_SIZE_RELATIVE_MAX,
                        }}
                        sx={{ width: 88 }}
                      />
                    </Stack>
                  );
                }}
              />
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard
          title={t('event.detail.settingsMenu.customize.layoutSection')}
          action={
            <ToggleButtonGroup
              exclusive
              size="small"
              value={marginsMode}
              onChange={(_e, value: 'percent' | 'px' | null) => {
                if (value) setMarginsMode(value);
              }}
            >
              <ToggleButton value="percent">{t('event.detail.settingsMenu.customize.unitPercent')}</ToggleButton>
              <ToggleButton value="px">{t('event.detail.settingsMenu.customize.unitPx')}</ToggleButton>
            </ToggleButtonGroup>
          }
        >
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            {t('event.detail.settingsMenu.customize.marginsHint')}
          </Typography>
          <Controller
            name="margins"
            control={form.control}
            render={({ field }) => {
              const displayMargins = convertMarginsForDisplay(field.value, marginsMode);
              return (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    gridTemplateRows: 'auto auto auto',
                    gap: 1,
                    maxWidth: 360,
                    mx: 'auto',
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'action.hover',
                  }}
                >
                  <Box sx={{ gridColumn: 2, gridRow: 1, justifySelf: 'center' }}>
                    {marginField('top', displayMargins, field.onChange, field.value)}
                  </Box>
                  <Box sx={{ gridColumn: 1, gridRow: 2, alignSelf: 'center' }}>
                    {marginField('left', displayMargins, field.onChange, field.value)}
                  </Box>
                  <Box
                    sx={{
                      gridColumn: 2,
                      gridRow: 2,
                      alignSelf: 'center',
                      justifySelf: 'center',
                      px: 2,
                      py: 3,
                      minWidth: 96,
                      textAlign: 'center',
                      borderRadius: 1.5,
                      bgcolor: 'background.paper',
                      border: '1px dashed',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      {t('event.detail.settingsMenu.customize.contentArea')}
                    </Typography>
                  </Box>
                  <Box sx={{ gridColumn: 3, gridRow: 2, alignSelf: 'center', justifySelf: 'end' }}>
                    {marginField('right', displayMargins, field.onChange, field.value)}
                  </Box>
                  <Box sx={{ gridColumn: 2, gridRow: 3, justifySelf: 'center' }}>
                    {marginField('bottom', displayMargins, field.onChange, field.value)}
                  </Box>
                </Box>
              );
            }}
          />
        </SectionCard>
      </Stack>

      <Box
        sx={{
          position: { lg: 'sticky' },
          top: { lg: 0 },
          alignSelf: 'start',
        }}
      >
        <CustomizeStickyPreview
          eventId={event?.id}
          formValues={stickyPreviewFormValues}
          initialSettings={initialSettings}
          logoPreviewUrl={getPreview('event-logo')}
          backgroundPreviewUrl={getPreview('background-image')}
          onFullscreenPreview={handlePreviewSettings}
          isFullscreenPreviewPending={isPreviewPending}
        />
      </Box>

      {previewData && (
        <PreviewModal
          open={previewModalOpen}
          onClose={() => setPreviewModalOpen(false)}
          playType={previewData.playType}
          playerSyncData={previewData.playerSyncData}
          title="Event Settings Preview"
        />
      )}
    </Box>
  );
}
